import type { SessionStatus } from '~/app/providers/SessionProvider'
import { ROUTES } from '~/shared/config'

export type RouteGuardInput = {
  status: SessionStatus
  /** Бэк сказал «доступа нет» — гейт подписки на канал закрыт. */
  isBlocked: boolean
  pathname: string
}

export type RouteGuardDecision =
  /** Сплэш: ещё не знаем, куда пускать. */
  | { kind: 'SPLASH' }
  | { kind: 'REDIRECT'; to: string }
  /** Обычная работа: рисуем таблицу маршрутов. */
  | { kind: 'ROUTES' }

/**
 * Единственное правило «куда пускать» — вынесено из компонента, потому что порядок этих
 * проверок и есть поведение приложения на входе, и его дешевле проверить тестом, чем глазами.
 *
 * Ключевое: пока статус сессии — не `ready`, любой адрес, кроме онбординга, уводит на онбординг.
 * Значит, экран регистрации имеет право закончиться переходом на главную только тогда, когда
 * сессия уже переключилась в `ready` (см. `SessionProvider.completeOnboarding`), иначе роутер
 * вернёт человека обратно и тот увидит лишний кадр первого шага.
 */
export function resolveRouteGuard(input: RouteGuardInput): RouteGuardDecision {
  const { status, isBlocked, pathname } = input

  if (status === 'loading') {
    return { kind: 'SPLASH' }
  }

  // Гейт подписки — поверх всего остального: пока доступа нет, других экранов не существует.
  const isGateRoute = pathname === ROUTES.gate

  if (isBlocked) {
    return isGateRoute ? { kind: 'ROUTES' } : { kind: 'REDIRECT', to: ROUTES.gate }
  }

  if (isGateRoute) {
    return { kind: 'REDIRECT', to: ROUTES.home }
  }

  const isOnboardingRoute = pathname.startsWith(ROUTES.onboarding)

  if (status !== 'ready') {
    return isOnboardingRoute ? { kind: 'ROUTES' } : { kind: 'REDIRECT', to: ROUTES.onboarding }
  }

  // Онбординг пройден — экран регистрации больше не существует, в том числе по прямой ссылке.
  if (isOnboardingRoute) {
    return { kind: 'REDIRECT', to: ROUTES.home }
  }

  return { kind: 'ROUTES' }
}
